import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import {
	LenderClientAssociation,
	LenderClientAssociationTypes,
} from '@components/lender-card/client-lender-card/client-lender-card.component';
import { ICON } from '@core/util/icon';
import {
	InsuranceAmountDescription,
	InsuranceAmountDropdownOptions,
	InterestCalculatedOptions,
	InterestRatePrimeOptions,
	InterestRateTypeOptions,
	PaymentFrequencyOptions,
	getMortgagePrimaryLender,
} from '@core/util/mortgage';
import { LawFirm } from '@models/lawFirms';
import { AssociatedContactType, Client } from '@models/clients';
import { MatterFileType, MatterClientType } from '@models/matter';
import { LawFirmEvent } from '@models/lawFirms';
import {
	InsuranceAmount,
	InterestRateType,
	MatterMortgageInstructionSource,
	MatterMortgagePriority,
	MatterMortgageWithAssociations,
	MortgageInterestCalculations,
	MortgagePaymentFrequencies,
	MortgagePercentageOfInterestTypes,
	TransactionType,
} from '@models/mortgage';
import {
	Correspondence,
	Lender,
	LenderArgs,
	MortgageeType,
} from '@models/lender';
import { UntilDestroy } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { ClientCreationService } from '@services/closer-api/client-creation/client-creation.service';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterMortgageDispatch } from '@state/matter/matter_mortgages';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';

@UntilDestroy()
@Component({
	selector: 'app-mortgage-intake-fields',
	templateUrl: './mortgage-intake-fields.component.html',
	styleUrls: ['./mortgage-intake-fields.component.scss'],
	standalone: false,
})
export class MortgageIntakeFieldsComponent {
	@Input() set selectedMortgage(mortgage: MatterMortgageWithAssociations) {
		this._selectedMortgage = mortgage;

		this.primaryLender = getMortgagePrimaryLender(mortgage);
		this.selectedInsuranceAmountText =
			InsuranceAmountDescription[this._selectedMortgage?.insuranceAmount];

		if (mortgage?.chargeType) {
			this.chargeTypeError = false;
		}

		if (this.primaryLender) {
			this.lenderError = false;
		}
	}

	@SelectSnapshot(SelectedMatter.isLeapMatter) isLeapMatter: boolean;
	@ViewSelectSnapshot(SelectedMatter.matterFileType)
	matterFileType: MatterFileType;
	@ViewSelectSnapshot(SelectedMatter.id) matterId: string;
	public _selectedMortgage: MatterMortgageWithAssociations;
	public readonly TransactionType = TransactionType;
	public ICON = ICON;

	public lenderError = false;
	public chargeTypeError = false;

	public paymentFrequencyOptions = PaymentFrequencyOptions;
	public interestRateTypeOptions = InterestRateTypeOptions;
	public interestCalculatedOptions = InterestCalculatedOptions;
	public insuranceAmountOptions = InsuranceAmountDropdownOptions;
	public selectedInsuranceAmountText: string;
	public guarantorClientType = MatterClientType.GUARANTOR;
	public variableRateType = InterestRateType.VARIABLE;
	public MatterFileType = MatterFileType;
	public MatterMortgageInstructionSource = MatterMortgageInstructionSource;
	public interestRatePrimeOptions = InterestRatePrimeOptions;
	public mortgageeTypes = MortgageeType;
	public transactionType = TransactionType;
	public primaryLender: Lender;
	public get showChargeTypeDropdown() {
		return !!Object.values(this.primaryLender?.chargeTypes ?? {})?.length;
	}

	public get borrowerClientType() {
		return this.matterFileType === MatterFileType.PURCHASE
			? MatterClientType.BUYER
			: MatterClientType.BORROWER;
	}
	public get valid() {
		return !this.lenderError && !this.chargeTypeError;
	}

	@ViewChild('lenderDetails')
	lenderDetailsComponent: ElementRef<HTMLDivElement>;

	@ViewChild('chargeType') chargeTypeDiv: ElementRef<HTMLDivElement>;

	constructor(
		private mortgageDispatch: MatterMortgageDispatch,
		private clientCreationService: ClientCreationService,
	) {}

	onMortgageeUpdated(mortgagee: Lender): void {
		this.mortgageDispatch.updateMortgage(this._selectedMortgage.id, {
			lenders: { [mortgagee.id]: mortgagee },
		});
	}

	onMortgageeRemove() {
		this.mortgageDispatch.RemoveAllMortgageLender(this._selectedMortgage.id);
	}

	setActingForMortgagee(value: boolean): void {
		this.mortgageDispatch.updateMortgage(this._selectedMortgage.id, {
			actingForMortgagee: value,
		});
	}

	setPriority(priority: MatterMortgagePriority): void {
		this.mortgageDispatch.updateMortgage(this._selectedMortgage.id, {
			priority,
		});
	}

	onSelectChargeType(chargeType: string): void {
		this.mortgageDispatch.SetMortgageFormsChargeType(
			this._selectedMortgage.id,
			chargeType,
		);
	}

	setCommitmentDate(value: string): void {
		this.mortgageDispatch.updateMortgage(this._selectedMortgage.id, {
			commitmentDate: value,
		});
	}

	setReferenceNumber(value: string): void {
		this.mortgageDispatch.updateMortgage(this._selectedMortgage.id, {
			referenceNumber: value,
		});
	}

	setTerm(value: string): void {
		this.mortgageDispatch.updateMortgage(this._selectedMortgage.id, {
			term: value,
		});
	}

	setAmortization(value: string): void {
		this.mortgageDispatch.updateMortgage(this._selectedMortgage.id, {
			amortization: value,
		});
	}

	setLoanAmount(value: number): void {
		this.mortgageDispatch.updateMortgage(this._selectedMortgage.id, {
			loanAmount: value,
		});
	}

	setStandardChargeTerm(value: string): void {
		this.mortgageDispatch.updateMortgage(this._selectedMortgage.id, {
			standardChargeTerm: value,
		});
	}

	setInsuranceAmount(value: InsuranceAmount): void {
		this.mortgageDispatch.updateMortgage(this._selectedMortgage.id, {
			insuranceAmount: value,
		});
	}

	setInterestOnlyPayments(value: boolean): void {
		this.mortgageDispatch.updateMortgage(this._selectedMortgage.id, {
			interestOnlyPayments: value,
		});
	}

	setFirstPaymentDate(value: string): void {
		this.mortgageDispatch.updateMortgage(this._selectedMortgage.id, {
			firstPaymentDate: value,
		});
	}

	setPaymentDue(value: string): void {
		this.mortgageDispatch.updateMortgage(this._selectedMortgage.id, {
			paymentDue: value,
		});
	}

	setMaturityDate(value: string): void {
		this.mortgageDispatch.updateMortgage(this._selectedMortgage.id, {
			maturityDate: value,
		});
	}

	setLastPaymentDate(value: string): void {
		this.mortgageDispatch.updateMortgage(this._selectedMortgage.id, {
			lastPaymentDate: value,
		});
	}

	setInterestAdjustmentDate(value: string): void {
		this.mortgageDispatch.updateMortgage(this._selectedMortgage.id, {
			interestAdjustmentDate: value,
		});
	}

	setPaymentFrequency(value: MortgagePaymentFrequencies): void {
		this.mortgageDispatch.updateMortgage(this._selectedMortgage.id, {
			paymentFrequency: value,
		});
	}

	selectPaymentFrequency(
		value: DropdownResult<DropdownOption<MortgagePaymentFrequencies>>,
	): void {
		if (value?.result?.value) {
			this.setPaymentFrequency(value.result.value);
		}
	}

	setRegularPayments(value: number): void {
		this.mortgageDispatch.updateMortgage(this._selectedMortgage.id, {
			regularPayments: value,
		});
	}

	setInterestCalculated(value: MortgageInterestCalculations): void {
		this.mortgageDispatch.updateMortgage(this._selectedMortgage.id, {
			interestCalculated: value,
		});
	}

	selectInterestCalculated(
		value: DropdownResult<DropdownOption<MortgageInterestCalculations>>,
	): void {
		if (value?.result?.value) {
			this.setInterestCalculated(value.result.value);
		}
	}

	setInterestRateType(value: InterestRateType): void {
		this.mortgageDispatch.updateMortgage(this._selectedMortgage.id, {
			interestRateType: value,
		});
	}

	setInterestRate(value: number): void {
		this.mortgageDispatch.updateMortgage(this._selectedMortgage.id, {
			interestRate: value,
		});
	}

	setDealId(value: string): void {
		this.mortgageDispatch.updateMortgage(this._selectedMortgage.id, {
			dealId: value,
		});
	}

	setMMS(value: boolean): void {
		this.mortgageDispatch.updateMortgage(this._selectedMortgage.id, {
			mms: value,
		});
	}

	setAbovePrime(value: boolean): void {
		this.mortgageDispatch.updateMortgage(this._selectedMortgage.id, {
			interestAbovePrime: value,
		});
	}

	setAssignmentOfRents(value: boolean): void {
		this.mortgageDispatch.updateMortgage(this._selectedMortgage.id, {
			assignmentOfRents: value,
		});
	}

	setGeneralSecurityAgreement(value: boolean): void {
		this.mortgageDispatch.updateMortgage(this._selectedMortgage.id, {
			generalSecurityAgreement: value,
		});
	}

	setRemarks(value: string): void {
		this.mortgageDispatch.updateMortgage(this._selectedMortgage.id, {
			remarks: value,
		});
	}

	addGuarantor(client: Client) {
		if (this.isLeapMatter) {
			// We need to call CreateGuarantor because the selected guarantor from LEAP needs to be created in Closer
			this.mortgageDispatch.CreateGuarantor(this._selectedMortgage.id, {
				client,
				matterClientRelationship: {
					clientTypes: [MatterClientType.GUARANTOR],
				},
			});
		} else {
			this.addGuarantorForNonLeap(client.id);
		}
	}

	addGuarantorForNonLeap(id: string) {
		this.mortgageDispatch.AddGuarantor(this._selectedMortgage.id, id);
	}

	createGuarantor(id: string) {
		if (this.isLeapMatter) {
			// We don't need to add the client because the component that triggered this event already adds the guarantor.
			// We may need to get photos for the client and import them to the matter but that has never been done for LEAP
			// clients. I have created a ticket to address that if we need to.
			// https://app.shortcut.com/lawlabs/story/28746/closer-guarantors-and-photos-and-borrowers
		} else {
			// I don't think we need to do this because the component that triggered this event already adds the guarantor.
			// I will not remove it as it is not related to this PR but should be looked at when looking at ticket below:
			// https://app.shortcut.com/lawlabs/story/28746/closer-guarantors-and-photos-and-borrowers
			this.addGuarantorForNonLeap(id);
		}
	}

	onUpdateCorrespondence(correspondence: Correspondence) {
		this.mortgageDispatch.updateMortgage(this._selectedMortgage.id, {
			correspondence,
		});
	}

	onSolicitorUpdated(updatedSolicitor: LawFirm) {
		if (updatedSolicitor) {
			this.mortgageDispatch.UpdateCorrespondenceSolicitor(
				this._selectedMortgage.id,
				updatedSolicitor,
				updatedSolicitor.leap?.personId,
				this.matterId,
				(updatedSolicitor as LawFirmEvent).role,
			);
		} else {
			this.mortgageDispatch.RemoveCorrespondence(this._selectedMortgage.id);
		}
	}
	onCorrespondenceRemoved() {
		this.mortgageDispatch.RemoveCorrespondence(this._selectedMortgage.id);
	}

	onLenderRemoved(lenderId: string) {
		this.mortgageDispatch.RemoveMortgageLender(
			this._selectedMortgage.id,
			lenderId,
		);
		this.mortgageDispatch.SetMortgageChargeType(
			this._selectedMortgage.id,
			null,
		);
	}

	onLenderUpdated(lender: Lender) {
		this.mortgageDispatch.AddMortgageLender(this._selectedMortgage.id, {
			lender: lender,
		});
	}

	onLenderAdded(lenderArgs: LenderArgs) {
		this.mortgageDispatch.AddMortgageLender(
			this._selectedMortgage.id,
			lenderArgs,
		);
	}

	onAddAssociation(association: LenderClientAssociation) {
		if (
			association.associationType ===
			LenderClientAssociationTypes.ASSOCIATED_CONTACT
		) {
			this.clientCreationService
				.AddExistingClientToAssociation({
					associatedContactType: AssociatedContactType.SIGNING_OFFICER,
					childClientId: association.associatingClientId,
					parentClientId: association.baseClientId,
					position: null,
				})
				.toPromise();
		}

		if (association.associationType === LenderClientAssociationTypes.INVESTOR) {
			this.mortgageDispatch.AddInvestorToLender(
				this._selectedMortgage.id,
				association.baseClientId,
				association.associatingClientId,
			);
		}
	}

	onRemoveInvestor({ lenderId, investorId }) {
		this.mortgageDispatch.RemoveInvestorFromLender(
			this._selectedMortgage.id,
			lenderId,
			investorId,
		);
	}

	onLenderSetAsPrimary(lenderId: string) {
		this.mortgageDispatch.SetMortgageLenderAsPrimary(
			this._selectedMortgage.id,
			lenderId,
		);
	}

	onPercentageOfInterestTypeUpdated(type: MortgagePercentageOfInterestTypes) {
		this.mortgageDispatch.SetPercentageOfInterestType(
			this._selectedMortgage.id,
			type,
		);
	}

	onLenderPercentageOfInterestUpdated(data: {
		lenderId: string;
		percentageOfInterest: string;
		percentageOfInterestType: MortgagePercentageOfInterestTypes;
	}) {
		this.mortgageDispatch.SetMortgageLenderPercentageOfInterestDetails(
			this._selectedMortgage.id,
			data.lenderId,
			data.percentageOfInterest,
			data.percentageOfInterestType,
		);
	}

	public triggerValidation(): void {
		if (
			!this.primaryLender &&
			this._selectedMortgage.transactionType !== TransactionType.VTB
		) {
			this.lenderError = true;
			this.lenderDetailsComponent.nativeElement.scrollIntoView({
				behavior: 'smooth',
			});
			return;
		}

		if (this.showChargeTypeDropdown && !this._selectedMortgage.chargeType) {
			this.chargeTypeError = true;
			this.chargeTypeDiv.nativeElement.scrollIntoView({ behavior: 'smooth' });
		}
	}
}
